<template>
  <div v-if="debug">
    Name: {{ name }}<br />
    Open Api: {{ openapi }}<br />
    DB Model: {{ dbModel }}<br />
    DB Actual: {{ actualTypeOfDbModel }}<br />
    Value: {{ value }}<i v-if="value === null">null</i><i v-if="value === undefined">undefined</i><br />
  </div>

  <ion-item v-if="isObjectTypeField(openapi)">
    <OptionalInput
      :nullable="!required"
      title="Object type"
      v-model="value"
      v-slot="{ slotValue, slotValueChanged, slotIsNull }"
    >
      <ion-segment
        @update:modelValue="slotValueChanged($event)"
        :value="slotValue"
        :disabled="slotIsNull && !required"
      >
        <ion-segment-button
          :value="1"
          title="If it can have other items inside."
        >
          <ion-label>Container</ion-label>
        </ion-segment-button>
        <ion-segment-button
          :value="2"
          title="Usually does not have other items inside."
        >
          <ion-label>Item</ion-label>
        </ion-segment-button>
      </ion-segment>
    </OptionalInput>
  </ion-item>
  <ion-item v-else-if="isBarcodeTypeField(openapi)">
    <OptionalInput
      :nullable="!required"
      title="Barcode Type"
      v-model="value"
      v-slot="{ slotValue, slotValueChanged, slotIsNull }"
    >
      <ion-segment
        @update:modelValue="slotValueChanged($event)"
        :value="slotValue"
        :disabled="slotIsNull && !required"
      >
        <ion-segment-button :value="1" title="Old school readers">
          <ion-label>Barcode</ion-label>
        </ion-segment-button>
        <ion-segment-button :value="2" title="Smartphones like those.">
          <ion-label>QR-Code</ion-label>
        </ion-segment-button>
      </ion-segment>
    </OptionalInput>
  </ion-item>
  <ion-item v-else-if="isIconField(openapi)">
    <OptionalInput
      :nullable="!required"
      :title="'title' in openapi ? openapi.title : 'Icon'"
      :linebreak="false"
      v-model="value"
      v-slot="{ slotValue, slotValueChanged, slotIsNull }"
    >
      <ChooseIconInput
        :model-value="slotValue ?? null"
        @update:modelValue="slotValueChanged"
        :disabled="slotIsNull && !required"
        embedded
      />
    </OptionalInput>
  </ion-item>
  <ChooseContainerInput
    v-else-if="
      dbModel !== null
      && typeof dbModel.referenceDict === 'object'
      && dbModel.referenceDict !== null
      && matchSchema(dbModel.referenceDict, 'Object')
      || isContainerIdField(openapi)
    "
    v-model="value"
  />
  <ion-item v-else-if="'format' in openapi && openapi.format === 'date-time'">
    <OptionalInput
      :nullable="!required"
      :title="openapi.title"
      :linebreak="false"
      v-model="value"
      v-slot="{ slotValue, slotValueChanged, slotIsNull }"
    >
      <ion-datetime
        :value="formatIonDatetimeValue(slotValue)"
        @ion-change="slotValueChanged($event.detail.value)"
        :disabled="slotIsNull && !required"
        locale="de-DE"
        hour-cycle="h23"
        :first-day-of-week="1"
      />
    </OptionalInput>
  </ion-item>
  <InputElement
    v-else-if="actualTypeOfDbModel !== null"
    :name="name"
    :openapi="actualTypeOfDbModel"
    :required="required"
    :multiline="multiline"
    v-model="value"
  >
    <!-- recursively go into the first element -->
  </InputElement>
  <ion-item v-else-if="'type' in openapi && openapi.type === 'integer'">
    <OptionalInput
      :nullable="!required"
      :title="openapi.title"
      v-model="value"
      v-slot="{ slotValue, slotValueChanged, slotIsNull }"
    >
      <ion-input
        clear-input
        :name="name"
        :title="openapi.title"
        :placeholder="`${name}…`"
        type="number"
        enterkeyhint="next"
        inputmode="numeric"
        :required="required"
        :value="slotValue"
        @update:modelValue="slotValueChanged($event)"
        @ion-change="slotValueChanged($event.detail.value)"
        :disabled="slotIsNull && !required"
      />
    </OptionalInput>
  </ion-item>
  <ion-item
    v-else-if="'type' in openapi && openapi.type === 'null'"
    :style="{ color: '#808080' }"
  >
    <ion-label v-if="'title' in openapi">{{ openapi.title }}</ion-label>
    <ion-label>automatic</ion-label>
  </ion-item>
  <ion-item v-else>
    <OptionalInput
      :nullable="!required"
      :title="openapi.title"
      :linebreak="hasSchemaType(openapi, 'string') && multiline"
      v-model="value"
      v-slot="{ slotValue, slotValueChanged, slotIsNull }"
    >
      <ion-textarea
        v-if="hasSchemaType(openapi, 'string') && multiline"
        :name="name"
        :title="openapi.title"
        :placeholder="`${name}…`"
        autocorrect="true"
        autocapitalize="none"
        enterkeyhint="enter"
        inputmode="text"
        :required="required"
        :value="slotValue"
        @update:modelValue="slotValueChanged($event)"
        @ion-change="slotValueChanged($event.detail.value)"
        :disabled="slotIsNull && !required"
        auto-grow
        :rows="4"
      />
      <ion-input
        v-else
        ref="nameInputRef"
        clear-input
        :name="name"
        :title="openapi.title"
        :placeholder="`${name}…`"
        type="text"
        autocorrect="on"
        :autocapitalize="name === 'name' && capitalizeEnabled ? 'words' : 'none'"
        enterkeyhint="next"
        inputmode="text"
        :required="required"
        :value="slotValue"
        @update:modelValue="slotValueChanged($event)"
        @ion-change="slotValueChanged($event.detail.value)"
        :disabled="slotIsNull && !required"
      >
        <ion-button
          v-if="name === 'name'"
          slot="start"
          fill="clear"
          size="small"
          :color="capitalizeEnabled ? 'primary' : 'medium'"
          @click.stop="toggleNameCapitalization"
          title="Toggle auto-capitalize"
        >Aa</ion-button>
      </ion-input>
    </OptionalInput>
  </ion-item>
</template>

<script setup lang="ts" generic="VALUE">
import type { ComputedRef } from "vue";
import { computed, defineComponent, nextTick, ref, watch } from "vue";
import {
  IonButton,
  IonInput,
  IonItem,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonDatetime,
  IonTextarea,
} from "@ionic/vue";
import ChooseIconInput from "@/components/ChooseIconInput.vue";
import ChooseContainerInput from "@/components/ChooseContainerInput.vue";
import {
  type UndefinedToNull,
  undefinedToNull,
} from "@/helper/undefinedToNull";
import OptionalInput from "@/components/OptionalInput.vue";
import type { dict } from "@/types";
import { useNameInputSettings } from "@/composables/useNameInputSettings";

const debug = ref<boolean>(false); // can be set in the inspector for debugging

defineComponent({
  name: "InputElement.vue",
});
const props = withDefaults(
  defineProps<{
    name: string,
    openapi: object,
    required: boolean,
    multiline?: boolean,
    modelValue: VALUE,
  }>(),
  {
    multiline: false,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: UndefinedToNull<VALUE>): void;
}>();

const { capitalizeNameField: capitalizeEnabled } = useNameInputSettings();
const nameInputRef = ref<any>(null);

async function focusNameInput() {
  await nextTick();
  await nameInputRef.value?.$el?.setFocus?.();
  await nameInputRef.value?.setFocus?.();
}

async function toggleNameCapitalization() {
  capitalizeEnabled.value = !capitalizeEnabled.value;
  await focusNameInput();
}

const value = ref<VALUE | null>(props.modelValue ?? null);
watch(value, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    emit("update:modelValue", undefinedToNull(newVal));
  }
});
type anyOfReference = {
  indexType: string;
  indexDict: dict;
  referenceType: string;
  referenceDict: dict;
};

/**
 * DB Models that use `anyOf` with exactly two entries, one being a type (pk), one being a `$ref` (model).
 * TL;DR: `Union[PK, Model]`, e.g. `int | Object`
 * Processes and neatly stores the `anyOf` tuple (id and the object referenced), if present.
 */
const dbModel: ComputedRef<anyOfReference | null> = computed(() => {
  // openapi = { anyOf: [ { type: 'integer' }, { $ref: '#/components/schemas/Object' } ] }
  // will return:
  // { indexType: 'integer', referenceType: '#/components/schemas/Object' }
  let result: anyOfReference | null = null;
  if (
    props.openapi !== undefined &&
    "anyOf" in props.openapi &&
    typeof props.openapi.anyOf === "object" &&
    props.openapi.anyOf !== null &&
    0 in props.openapi.anyOf &&
    1 in props.openapi.anyOf &&
    !(2 in props.openapi.anyOf) && // cheap length == 2
    typeof props.openapi.anyOf === "object" &&
    Array.isArray(props.openapi.anyOf) &&
    props.openapi.anyOf.length == 2 &&
    props.openapi.anyOf[0] &&
    props.openapi.anyOf[1]
  ) {
    console.log("IT IS A $ref");
    // for loop to ignore order
    for (let iA = 0; iA <= 1; iA++) {
      const iB = iA ? 0 : 1;
      // either iA = 0, ib = 1
      // or     iA = 1, iB = 0
      if (
        "type" in props.openapi.anyOf[iA] &&
        props.openapi.anyOf[iA].type === "integer" &&
        typeof props.openapi.anyOf[iB] === "object" &&
        props.openapi.anyOf[iB] !== null &&
        "$ref" in props.openapi.anyOf[iB] &&
        props.openapi.anyOf[iB]["$ref"]
      ) {
        result = {
          indexType: props.openapi.anyOf[iA].type,
          indexDict: props.openapi.anyOf[iA],
          referenceType: props.openapi.anyOf[iB]["$ref"],
          referenceDict: props.openapi.anyOf[iB],
        };
      }
    }
  }
  return result;
});

/**
 * Computes the openapi object for a recursive call in case of the type being an `anyOf` tuple,
 * with the id and the object.
 */
const actualTypeOfDbModel: ComputedRef<dict | null> = computed(() => {
  if (dbModel.value === null) {
    return null;
  }
  return Object.assign(
    { title: props.openapi.title, required: [] },
    dbModel.value.indexDict
  );
});

function formatIonDatetimeValue(value: string): string {
  return typeof value === "string"
    ? value.replace(/\.\d+(?=Z|[+-]\d{2}:?\d{2}$|$)/, "")
    : value;
}

function matchSchema(openapi: any, type: string): boolean {
  const fullComponentPath = `#/components/schemas/${type}`;
  if (!openapi) {
    return false;
  }
  if ("$ref" in openapi && openapi["$ref"] === fullComponentPath) {
    return true;
  }
  return ["allOf", "anyOf", "oneOf"].some(
    (key) =>
      key in openapi &&
      Array.isArray(openapi[key]) &&
      openapi[key].some((schema: any) => matchSchema(schema, type))
  );
}

function schemaContains(openapi: any, predicate: (schema: any) => boolean): boolean {
  if (!openapi) {
    return false;
  }
  if (predicate(openapi)) {
    return true;
  }
  return ["allOf", "anyOf", "oneOf"].some(
    (key) =>
      key in openapi &&
      Array.isArray(openapi[key]) &&
      openapi[key].some((schema: any) => schemaContains(schema, predicate))
  );
}

function hasSchemaType(openapi: any, type: string): boolean {
  return schemaContains(openapi, (schema) => "type" in schema && schema.type === type);
}

function isObjectTypeField(openapi: any): boolean {
  return matchSchema(openapi, "ObjectType") || (props.name === "type" && hasSchemaType(openapi, "integer"));
}

function isBarcodeTypeField(openapi: any): boolean {
  return matchSchema(openapi, "BarcodeType") || (props.name === "barcode_type" && hasSchemaType(openapi, "integer"));
}

function isIconField(openapi: any): boolean {
  return matchSchema(openapi, "FontAwesomeIcon") || (props.name === "logo" && hasSchemaType(openapi, "object"));
}

function isContainerIdField(openapi: any): boolean {
  return ["contained_in_object", "box__id"].includes(props.name) && hasSchemaType(openapi, "integer");
}

</script>

<style scoped lang="scss">
ion-segment {
  margin-top: 1em;
  margin-bottom: 1em;
}
</style>
